import { useState, useEffect } from 'react'
import Modal from './Modal'
import { updateMediaGroup, getApiErrorMessage } from '../api'
import { useI18n } from '../i18n'
import { useToast } from '../contexts/ToastContext'
import '../styles/components/media-edit-modal.css'

const PRICE_MIN = 0
const PRICE_MAX = 200

/**
 * Модалка редактирования группы медиа: название и диапазон цены.
 */
export default function GroupEditModal({
  open,
  modelId,
  sessionId,
  group,
  onClose,
  onSaved,
}) {
  const { t } = useI18n()
  const { addToast } = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [priceMin, setPriceMin] = useState(PRICE_MIN)
  const [priceMax, setPriceMax] = useState(PRICE_MAX)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [previewHint, setPreviewHint] = useState('')

  useEffect(() => {
    if (open && group) {
      setName(group.name ?? '')
      setDescription(group.description ?? '')
      const pmin = typeof group.price_min === 'number' ? group.price_min : PRICE_MIN
      const pmax = typeof group.price_max === 'number' ? group.price_max : PRICE_MAX
      setPriceMin(pmin)
      setPriceMax(pmax)
      setError('')
      setPreviewHint('')
    }
  }, [open, group?.id, group?.name, group?.description, group?.price_min, group?.price_max])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!modelId || !sessionId || !group?.id) return
    setSaving(true)
    setError('')
    try {
      const normMin = Math.min(Math.max(priceMin, PRICE_MIN), PRICE_MAX)
      const normMax = Math.min(Math.max(priceMax, PRICE_MIN), PRICE_MAX)
      const [min, max] = normMin <= normMax ? [normMin, normMax] : [normMax, normMin]
      const mediaList = Array.isArray(group.media) ? group.media : []
      const hasPaid = mediaList.some((m) => m.is_paid)
      const priced = min > 0 || max > 0
      if (priced && !hasPaid) {
        setError(t('media.group.validation.pricedNeedsPaid'))
        setSaving(false)
        return
      }
      if (!priced && hasPaid) {
        setError(t('media.group.validation.freeGroupHasPaid'))
        setSaving(false)
        return
      }
      const hasFreePreview = mediaList.some((m) => !m.is_paid)
      if (priced && !hasFreePreview) {
        setPreviewHint(t('media.group.validation.noPreviewHint'))
      } else {
        setPreviewHint('')
      }
      const updated = await updateMediaGroup(modelId, sessionId, group.id, {
        name: name.trim() || null,
        description: description.trim() || null,
        price_min: min,
        price_max: max,
      })
      onSaved?.({ ...group, ...updated })
      onClose?.()
      addToast(t('toast.media.saved'), 'success')
    } catch (err) {
      const msg = getApiErrorMessage(err, t('media.edit.modal.error'))
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  if (!open || !group?.id) return null

  return (
    <Modal
      open={open}
      title={t('media.group.edit.title')}
      onClose={onClose}
      footer={
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn-secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="group-edit-form" className="modal-btn modal-btn-primary" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      }
    >
      <form id="group-edit-form" className="media-edit-form" onSubmit={handleSubmit}>
        {error && <p className="media-edit-error">{error}</p>}
        {previewHint && !error && <p className="media-edit-error" style={{ opacity: 0.85 }}>{previewHint}</p>}
        <label className="media-edit-label">
          {t('media.group.namePlaceholder')}
          <input
            type="text"
            className="media-edit-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('media.group.namePlaceholder')}
          />
        </label>
        <label className="media-edit-label media-edit-label--description">
          {t('media.group.description', 'Step description')}
          <textarea
            className="media-edit-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('media.group.description.placeholder', 'Describe the whole step: what the buyer gets, sales angle, and any usage notes.')}
            rows={3}
          />
        </label>
        <label className="media-edit-label">
          {t('media.edit.price')}
          <div className="media-edit-price-inputs">
            <input
              type="number"
              min={PRICE_MIN}
              max={PRICE_MAX}
              value={priceMin}
              onChange={(e) => setPriceMin(Math.min(Math.max(parseInt(e.target.value, 10) || 0, PRICE_MIN), PRICE_MAX))}
              className="media-edit-input media-edit-input--price"
            />
            <span className="media-edit-price-sep">–</span>
            <input
              type="number"
              min={PRICE_MIN}
              max={PRICE_MAX}
              value={priceMax}
              onChange={(e) => setPriceMax(Math.min(Math.max(parseInt(e.target.value, 10) || 0, PRICE_MIN), PRICE_MAX))}
              className="media-edit-input media-edit-input--price"
            />
          </div>
        </label>
      </form>
    </Modal>
  )
}
